import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Globe, User, AlertCircle, Eye } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (s) => {
  if (s?.logo_file_id) {
    const tok = encodeURIComponent(localStorage.getItem("weave_token") || "");
    return `${BACKEND}/api/files/${s.logo_file_id}/download?auth=${tok}`;
  }
  return s?.logo_url || "";
};

export default function MySponsor() {
  const [sponsor, setSponsor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const previewAsStudent = new URLSearchParams(location.search).get("preview") === "student";
  const previewAs = new URLSearchParams(location.search).get("preview_as");
  const realIsAdmin = user?.role === "admin" || user?.role === "faculty";
  const isAdmin = realIsAdmin && !previewAsStudent && !previewAs;

  useEffect(() => {
    Promise.all([
      api.get(previewAs ? `/my-sponsor?as=${previewAs}` : "/my-sponsor").then(({ data }) => data).catch(() => null),
      api.get("/programme").then(({ data }) => data).catch(() => ({})),
    ]).then(([s, prog]) => {
      setSponsor(s);
      setRevealed(!!prog?.assignments_revealed);
    }).finally(() => setLoading(false));
  }, [previewAs]);

  if (loading) return <div className="text-sm text-[#525252]" data-testid="my-sponsor-loading">Loading…</div>;

  const logo = logoSrc(sponsor);
  const gated = !revealed && !isAdmin;

  return (
    <div className="space-y-6" data-testid="my-sponsor-page">
      {previewAsStudent && realIsAdmin && (
        <div className="border-2 border-[#f4b847] bg-[#FEF9E7] p-3 flex items-center gap-2 text-xs text-[#7A5B17]" data-testid="my-sponsor-preview-banner">
          <Eye size={14} /> <strong>Preview mode</strong> · you&apos;re seeing what students see before Session 2 reveal.
          <a href="/my-sponsor" className="underline hover:no-underline ml-auto">Exit preview</a>
        </div>
      )}
      {previewAs && realIsAdmin && (
        <div className="border-2 border-[#2596be] bg-[#E0F2FF] p-3 flex items-center gap-2 text-xs text-[#1e5077]" data-testid="my-sponsor-impersonate-banner">
          <Eye size={14} /> <strong>Impersonating student</strong> · you&apos;re seeing the exact sponsor this student will unlock at Session 2.
          <a href="/my-sponsor" className="underline hover:no-underline ml-auto">Exit preview</a>
        </div>
      )}
      {/* Team card + problem statement — ALWAYS rendered when caller is on a team,
          regardless of whether sponsor is assigned yet. */}
      <TeamProblemStatement />

      {/* Sponsor gated behind Session 2 reveal (admin bypasses) */}
      {gated ? (
        <div className="border-2 border-[#f4b847] bg-[#FEF9E7] p-8" data-testid="my-sponsor-locked">
          <div className="flex items-center gap-3">
            <AlertCircle size={22} className="text-[#7A5B17]" />
            <div>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#7A5B17]">Locked · Session 2 reveal</div>
              <h2 className="font-display text-2xl font-bold text-[#7A5B17] mt-1">Your sponsor is revealed at Session 2.</h2>
              <p className="text-sm text-[#7A5B17] mt-2 max-w-xl">
                Sponsor logo, brief and point-of-contact appear here the moment your admin flips the Session 2 reveal.
              </p>
            </div>
          </div>
        </div>
      ) : !sponsor ? (
        <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-8" data-testid="my-sponsor-empty">
          <div className="flex items-center gap-3">
            <AlertCircle size={22} className="text-[#7A5B17]" />
            <div>
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#7A5B17]">Not assigned yet</div>
              <h2 className="font-display text-2xl font-bold text-[#7A5B17] mt-1">No sponsor assigned to your team.</h2>
              <p className="text-sm text-[#7A5B17] mt-2 max-w-xl">
                Your admin will assign your team&apos;s sponsor before Session 2 (29 October) when the businesses present their challenges. Check back then.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="border border-[#0F1F2E] bg-white p-6 md:p-8" data-testid="my-sponsor-card">
            <div className="flex items-start gap-5 flex-wrap">
              {logo && (
                <img src={logo} alt={sponsor.name}
                  className="w-24 h-24 object-contain border border-[#E5E7EB] bg-white p-2 flex-shrink-0"
                  data-testid="sponsor-logo"/>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#25AAAA]">Your sponsor</div>
                <h1 className="font-display text-3xl font-black tracking-tight text-[#0F1F2E] mt-1" data-testid="sponsor-name">{sponsor.name}</h1>
                {sponsor.website && (
                  <a href={sponsor.website.startsWith("http") ? sponsor.website : `https://${sponsor.website}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sm text-[#25AAAA] hover:underline inline-flex items-center gap-1.5 mt-2"
                    data-testid="sponsor-website">
                    <Globe size={14} /> {sponsor.website}
                  </a>
                )}
              </div>
            </div>

            {sponsor.brief && (
              <div className="mt-6">
                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-2">The sponsor challenge</div>
                <div className="text-sm text-[#0F1F2E] leading-relaxed whitespace-pre-wrap" data-testid="sponsor-brief">{sponsor.brief}</div>
              </div>
            )}
          </div>

          {(() => {
            const contacts = [
              { name: sponsor.representative_name, role: sponsor.representative_role, bio: sponsor.representative_bio, key: "1" },
              { name: sponsor.representative2_name, role: sponsor.representative2_role, bio: sponsor.representative2_bio, key: "2" },
            ].filter((c) => c.name);
            if (contacts.length === 0) return null;
            return (
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="sponsor-rep-card">
                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-4">
                  {contacts.length > 1 ? "Your points of contact" : "Your point of contact"}
                </div>
                <div className={`grid gap-6 ${contacts.length > 1 ? "md:grid-cols-2" : ""}`}>
                  {contacts.map((c) => (
                    <div key={c.key} className="flex items-start gap-4" data-testid={`sponsor-rep-${c.key}`}>
                      <div className="w-11 h-11 bg-[#0F1F2E] text-white flex items-center justify-center flex-shrink-0">
                        <User size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-xl font-bold text-[#0F1F2E]" data-testid={`sponsor-rep-name-${c.key}`}>{c.name}</div>
                        {c.role && <div className="text-sm text-[#25AAAA] font-semibold mt-0.5" data-testid={`sponsor-rep-role-${c.key}`}>{c.role}</div>}
                        {c.bio && <p className="text-sm text-[#525252] mt-2 leading-relaxed whitespace-pre-wrap" data-testid={`sponsor-rep-bio-${c.key}`}>{c.bio}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-[#525252] mt-5 border-t border-[#E5E7EB] pt-3">
                  Questions for your sponsor go through <strong>My Team → Questions for your sponsor</strong> — they're compiled and sent in one batch.
                </p>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// Team custom-name + problem-statement — visible whenever the caller is on a team.
function TeamProblemStatement() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [ps, setPs] = useState("");
  const [customName, setCustomName] = useState("");
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTeam = async () => {
    try {
      const { data } = await api.get("/teams/my");
      setTeam(data?.team || null);
      setPs(data?.team?.problem_statement || "");
      setCustomName(data?.team?.custom_name || "");
    } catch { /* no team */ }
  };
  useEffect(() => { loadTeam(); }, []);

  if (!team) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/teams/${team.id}/problem-statement`, { problem_statement: ps });
      setEditing(false);
      await loadTeam();
    } finally { setSaving(false); }
  };

  const rename = async () => {
    setSaving(true);
    try {
      await api.put(`/teams/my/name`, { custom_name: customName.trim() });
      setRenaming(false);
      await loadTeam();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Team custom name */}
      <div className="border border-[#0F1F2E] bg-white p-6" data-testid="team-name-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Your team</div>
            <h3 className="font-display text-2xl font-black tracking-tight text-[#0F1F2E] mt-1" data-testid="team-name-display">
              {team.name}{team.custom_name ? <span className="text-[#25AAAA]"> · {team.custom_name}</span> : <span className="text-[#525252] italic font-normal text-lg"> · Give your team a name</span>}
            </h3>
          </div>
          {!renaming && (
            <Button variant="outline" className="rounded-none" onClick={() => setRenaming(true)} data-testid="team-name-edit">
              {team.custom_name ? "Change name" : "Name our team"}
            </Button>
          )}
        </div>
        {renaming && (
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <Input value={customName} onChange={(e) => setCustomName(e.target.value)}
              placeholder="e.g. Firebolt, The Peregrines…" className="rounded-none flex-1 min-w-[220px]"
              data-testid="team-name-input"/>
            <Button className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" disabled={saving} onClick={rename} data-testid="team-name-save">
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => { setRenaming(false); setCustomName(team.custom_name || ""); }}>Cancel</Button>
          </div>
        )}
      </div>

      {/* Problem statement */}
      <div className="border border-[#25AAAA] bg-[#E6F7F7] p-6" data-testid="problem-statement-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#1e7d7d]">Your team&apos;s problem statement</div>
            <p className="text-xs text-[#1e7d7d] mt-1">Visible to your team, your admin and the mentors you&apos;ve booked. Not seen by other teams.</p>
          </div>
          {!editing && (
            <Button variant="outline" className="rounded-none border-[#1e7d7d] text-[#1e7d7d] hover:bg-[#1e7d7d] hover:text-white"
              onClick={() => setEditing(true)} data-testid="problem-statement-edit">
              {ps ? "Edit" : "Write it"}
            </Button>
          )}
        </div>
        {editing ? (
          <div className="mt-3 space-y-3">
            <Textarea rows={4} value={ps} onChange={(e) => setPs(e.target.value)}
              placeholder="One sentence — the problem you're solving for the sponsor."
              data-testid="problem-statement-input"/>
            <div className="flex gap-2">
              <Button className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]" disabled={saving} onClick={save} data-testid="problem-statement-save">
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button variant="outline" className="rounded-none" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-sm text-[#0F1F2E] whitespace-pre-wrap min-h-[1.5rem]" data-testid="problem-statement-body">
            {ps || <span className="italic text-[#525252]">Not written yet.</span>}
          </div>
        )}
      </div>
    </div>
  );
}
